// Converts the MathML that docx imports produce into LaTeX, so the question editor can hold it as an
// editable (KaTeX) formula. Covers the constructs found in question banks; anything else makes the
// conversion return null and the caller keeps the original MathML untouched.

class Unsupported extends Error {}

const GREEK = {
  α: "\\alpha", β: "\\beta", γ: "\\gamma", δ: "\\delta", ε: "\\varepsilon", ϵ: "\\epsilon", ζ: "\\zeta",
  η: "\\eta", θ: "\\theta", ϑ: "\\vartheta", ι: "\\iota", κ: "\\kappa", λ: "\\lambda", μ: "\\mu", ν: "\\nu",
  ξ: "\\xi", π: "\\pi", ρ: "\\rho", σ: "\\sigma", τ: "\\tau", υ: "\\upsilon", ϕ: "\\phi", φ: "\\varphi",
  χ: "\\chi", ψ: "\\psi", ω: "\\omega", Γ: "\\Gamma", Δ: "\\Delta", Θ: "\\Theta", Λ: "\\Lambda", Ξ: "\\Xi",
  Π: "\\Pi", Σ: "\\Sigma", Φ: "\\Phi", Ψ: "\\Psi", Ω: "\\Omega",
};

const SYMBOLS = {
  "∞": "\\infty", "≤": "\\le", "≥": "\\ge", "≠": "\\ne", "≈": "\\approx", "≡": "\\equiv", "∼": "\\sim",
  "≅": "\\cong", "∝": "\\propto", "∈": "\\in", "∉": "\\notin", "∋": "\\ni", "⊂": "\\subset", "⊆": "\\subseteq",
  "⊃": "\\supset", "⊇": "\\supseteq", "⊄": "\\not\\subset", "∪": "\\cup", "∩": "\\cap", "∅": "\\varnothing",
  "×": "\\times", "÷": "\\div", "·": "\\cdot", "⋅": "\\cdot", "∙": "\\cdot", "±": "\\pm", "∓": "\\mp",
  "→": "\\to", "←": "\\leftarrow", "↔": "\\leftrightarrow", "⇒": "\\Rightarrow", "⇐": "\\Leftarrow",
  "⇔": "\\Leftrightarrow", "↦": "\\mapsto", "∑": "\\sum", "∏": "\\prod", "∫": "\\int", "∬": "\\iint",
  "∮": "\\oint", "√": "\\surd", "∀": "\\forall", "∃": "\\exists", "∂": "\\partial", "∇": "\\nabla",
  "∠": "\\angle", "°": "^\\circ", "′": "\\prime", "″": "\\prime\\prime", "…": "\\ldots", "⋯": "\\cdots",
  "⋮": "\\vdots", "⋱": "\\ddots", "∴": "\\therefore", "∵": "\\because", "⊥": "\\perp", "∥": "\\parallel",
  "∣": "\\mid", "‖": "\\|", "⟨": "\\langle", "⟩": "\\rangle", "⌊": "\\lfloor", "⌋": "\\rfloor",
  "⌈": "\\lceil", "⌉": "\\rceil", "{": "\\{", "}": "\\}", "−": "-", "–": "-", "∖": "\\setminus",
  "∘": "\\circ", "⊕": "\\oplus", "⊗": "\\otimes", "≪": "\\ll", "≫": "\\gg", "∧": "\\wedge", "∨": "\\vee",
  "¬": "\\neg", "ℝ": "\\mathbb{R}", "ℕ": "\\mathbb{N}", "ℤ": "\\mathbb{Z}", "ℚ": "\\mathbb{Q}",
  "ℂ": "\\mathbb{C}", "~": "\\sim", "\\": "\\backslash", "^": "\\wedge",
  "#": "\\#", $: "\\$", "%": "\\%", "&": "\\&", _: "\\_",
};

const FUNCTIONS = new Set([
  "sin", "cos", "tan", "cot", "sec", "csc", "sinh", "cosh", "tanh", "coth", "arcsin", "arccos", "arctan",
  "log", "ln", "lg", "exp", "lim", "max", "min", "sup", "inf", "det", "gcd", "arg", "deg", "dim", "ker", "Pr",
]);
const BIG_OPERATORS = new Set(["∑", "∏", "∫", "∬", "∮"]);
const ACCENTS = { "¯": "\\overline", "‾": "\\overline", "―": "\\overline", "^": "\\hat", "ˆ": "\\hat", "→": "\\vec", "~": "\\tilde", "˜": "\\tilde", ".": "\\dot", "˙": "\\dot" };
const INVISIBLE = /[⁡-⁤]/g;

const symbol = (ch) => GREEK[ch] ?? SYMBOLS[ch] ?? ch;
const textOf = (el) => (el.textContent || "").replace(INVISIBLE, "");
const kids = (el) => Array.from(el.children);

// Puts a space after a trailing \command when a letter follows, so "\sin" + "x" doesn't become "\sinx".
const join = (parts) =>
  parts.reduce((acc, p) => {
    if (!p) return acc;
    return (/\\[a-zA-Z]+$/.test(acc) && /^[a-zA-Z]/.test(p) ? `${acc} ` : acc) + p;
  }, "");

// A script/argument needs braces unless it is a single token.
const grp = (s) => (/^(\\[a-zA-Z]+|[^\s{}\\])$/.test(s) ? s : `{${s}}`);

const escapeText = (s) => s.replace(/ /g, " ").replace(/[\\{}#$%&_^~]/g, (c) => `\\${c === "\\" ? "textbackslash " : c}`);

const isOp = (el, ch) => el && el.localName === "mo" && textOf(el).trim() === ch;

const matrixEnv = (open, close) => {
  if (open === "(" && close === ")") return "pmatrix";
  if (open === "[" && close === "]") return "bmatrix";
  if (open === "|" && close === "|") return "vmatrix";
  if (open === "{" && !close) return "cases";
  return null;
};

const table = (el, env = "matrix") => {
  const rows = kids(el).map((tr) => kids(tr).map((td) => convSeq(kids(td))).join(" & "));
  return `\\begin{${env}}${rows.join(" \\\\ ")}\\end{${env}}`;
};

function convSeq(nodes) {
  const parts = [];
  for (let i = 0; i < nodes.length; i += 1) {
    // Blank <mtext> tokens are typed spaces from Word; they only widen the equation (see questionHtml.js).
    if (nodes[i].localName === "mtext" && !textOf(nodes[i]).trim() && nodes[i - 1]?.localName !== "mtext" && nodes[i + 1]?.localName !== "mtext") {
      continue;
    }
    // A "{" followed by a table is a piecewise definition.
    if (isOp(nodes[i], "{") && nodes[i + 1]?.localName === "mtable") {
      parts.push(table(nodes[i + 1], "cases"));
      i += 1;
    } else {
      parts.push(conv(nodes[i]));
    }
  }
  return join(parts);
}

function conv(el) {
  const tag = el.localName;
  const c = kids(el);
  switch (tag) {
    case "math":
    case "mrow":
    case "mstyle":
    case "mpadded":
    case "menclose":
      return convSeq(c);
    case "semantics":
      return c.length ? conv(c[0]) : "";
    case "mphantom":
      return "";
    case "mi": {
      const t = textOf(el).trim();
      if (!t) return "";
      const variant = el.getAttribute("mathvariant");
      if ([...t].length === 1) {
        if (variant === "normal") return `\\mathrm{${symbol(t)}}`;
        if (variant === "bold") return `\\mathbf{${symbol(t)}}`;
        if (variant === "double-struck") return `\\mathbb{${t}}`;
        return symbol(t);
      }
      if (FUNCTIONS.has(t)) return `\\${t}`;
      if (/^[A-Za-z]+$/.test(t)) return `\\operatorname{${t}}`;
      return join([...t].map(symbol));
    }
    case "mn":
      return textOf(el).trim();
    case "mo": {
      const t = textOf(el).trim();
      return t ? symbol(t) : "";
    }
    case "mtext":
    case "ms": {
      const t = textOf(el);
      if (!t) return "";
      return t.trim() ? `\\text{${escapeText(t)}}` : "\\ ";
    }
    case "mspace":
      return "\\,";
    case "mfrac": {
      if (c.length !== 2) throw new Unsupported();
      const [a, b] = [conv(c[0]), conv(c[1])];
      return el.getAttribute("linethickness") === "0" ? `\\binom{${a}}{${b}}` : `\\frac{${a}}{${b}}`;
    }
    case "msup":
      if (c.length !== 2) throw new Unsupported();
      return `${grp(conv(c[0]))}^${grp(conv(c[1]))}`;
    case "msub":
      if (c.length !== 2) throw new Unsupported();
      return `${grp(conv(c[0]))}_${grp(conv(c[1]))}`;
    case "msubsup":
      if (c.length !== 3) throw new Unsupported();
      return `${grp(conv(c[0]))}_${grp(conv(c[1]))}^${grp(conv(c[2]))}`;
    case "msqrt":
      return `\\sqrt{${convSeq(c)}}`;
    case "mroot":
      if (c.length !== 2) throw new Unsupported();
      return `\\sqrt[${conv(c[1])}]{${conv(c[0])}}`;
    case "munder":
    case "mover":
    case "munderover": {
      const base = c[0];
      const big = BIG_OPERATORS.has(textOf(base).trim()) || (base.localName === "mi" && textOf(base).trim() === "lim");
      const b = conv(base);
      if (tag === "munderover") return `${b}_${grp(conv(c[1]))}^${grp(conv(c[2]))}`;
      const arg = conv(c[1]);
      if (big) return `${b}${tag === "munder" ? "_" : "^"}${grp(arg)}`;
      if (tag === "mover" && ACCENTS[textOf(c[1]).trim()]) return `${ACCENTS[textOf(c[1]).trim()]}{${b}}`;
      return tag === "munder" ? `\\underset{${arg}}{${b}}` : `\\overset{${arg}}{${b}}`;
    }
    case "mfenced": {
      const open = el.getAttribute("open") ?? "(";
      const close = el.getAttribute("close") ?? ")";
      const seps = [...(el.getAttribute("separators") ?? ",")].filter((s) => s.trim());
      if (c.length === 1 && c[0].localName === "mtable") {
        const env = matrixEnv(open, close);
        if (env) return table(c[0], env);
      }
      const inner = c.map((k, i) => (i < c.length - 1 && seps.length ? conv(k) + symbol(seps[Math.min(i, seps.length - 1)]) : conv(k)));
      return join([symbol(open), join(inner), symbol(close)]);
    }
    case "mtable":
      return table(el);
    default:
      throw new Unsupported();
  }
}

// Returns LaTeX for a <math> element, or null when it uses something this converter doesn't cover.
export function mathmlToLatex(math) {
  try {
    const tex = math.querySelector('annotation[encoding="application/x-tex"]');
    if (tex?.textContent.trim()) return tex.textContent.trim();
    const latex = conv(math).replace(/\s+/g, " ").trim();
    return latex || null;
  } catch (e) {
    if (e instanceof Unsupported) return null;
    throw e;
  }
}
